import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import type {
  AdminDeskReview,
  DeskReviewAction,
  DeskReviewModerationStatus,
  DeskReviewSummary,
  Page,
} from "@/types";

/**
 * React Query hooks for admin Desk review moderation (KC-509). The summary
 * (counts per status + who may moderate) also feeds the sidebar's pending
 * badge, so every action invalidates the whole slice.
 */

export interface DeskReviewsListParams {
  status: DeskReviewModerationStatus;
  page: number;
  page_size: number;
}

export const deskReviewKeys = {
  all: ["admin", "desk-reviews"] as const,
  summary: () => [...deskReviewKeys.all, "summary"] as const,
  list: (params: DeskReviewsListParams) => [...deskReviewKeys.all, "list", params] as const,
};

export function useDeskReviews(params: DeskReviewsListParams) {
  return useQuery<Page<AdminDeskReview>>({
    queryKey: deskReviewKeys.list(params),
    queryFn: () => api.getAdminDeskReviews(params),
  });
}

export function useDeskReviewSummary() {
  return useQuery<DeskReviewSummary>({
    queryKey: deskReviewKeys.summary(),
    queryFn: () => api.getDeskReviewSummary(),
  });
}

export function useModerateDeskReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: DeskReviewAction }) =>
      api.moderateDeskReview(id, action),
    onSettled: () => queryClient.invalidateQueries({ queryKey: deskReviewKeys.all }),
  });
}
